import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  ApiOutlined,
  ChatBubbleOutlineOutlined,
  CloseOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ExtensionOutlined,
  MenuBookOutlined,
} from '@mui/icons-material';

import useApiCall from '../../hooks/useApiCall';
import LoadingSpinner from '../../components/LoadingSpinner';
import { INDIGO, SLATE } from '../../constants/Colors';
import { usePageMeta } from '../../contexts/PageMetaContext';

// ── Visual config ──────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  rag: { label: 'RAG', color: '#16a34a', bg: '#f0fdf4', Icon: MenuBookOutlined },
  http: { label: 'HTTP', color: '#2563eb', bg: '#eff6ff', Icon: ApiOutlined },
  handoff: { label: 'Handoff', color: '#7c3aed', bg: '#f5f3ff', Icon: ChatBubbleOutlineOutlined },
};

function typeConfig(type) {
  return (
    TYPE_CONFIG[type] || { label: type, color: SLATE[500], bg: SLATE[100], Icon: ExtensionOutlined }
  );
}

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.875rem',
    bgcolor: '#ffffff',
    '& fieldset': { borderColor: SLATE[300] },
    '&:hover fieldset': { borderColor: SLATE[400] },
    '&.Mui-focused fieldset': { borderColor: INDIGO[500] },
  },
  '& .MuiInputBase-input': { color: SLATE[900] },
};

// ── Field names that need multiline / JSON treatment ──────────────────────────

const JSON_FIELDS = new Set(['tool_input_args_schema', 'api_header']);
const LONG_FIELDS = new Set([
  'api_payload',
  'tool_description',
  'tool_input',
  'tool_output',
  'tool_input_args_schema',
  'api_header',
]);

// ── Dynamic param field — driven by the schema param object ───────────────────

function ParamField({ fieldKey, schemaParam, value, onChange }) {
  const type = schemaParam?.type || 'string';
  const label =
    schemaParam?.label || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const helperText = schemaParam?.description || undefined;
  const placeholder = schemaParam?.placeholder ?? (schemaParam?.default != null ? String(schemaParam.default) : undefined);
  const options = schemaParam?.options;
  const isBoolean = type === 'boolean';
  const isNumber = type === 'number' || type === 'integer';
  const isJson = JSON_FIELDS.has(fieldKey);
  const isLong = LONG_FIELDS.has(fieldKey);

  // Select — when field has a fixed option list
  if (options?.length) {
    return (
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: '0.875rem' }}>{label}</InputLabel>
        <Select
          value={value ?? schemaParam?.default ?? ''}
          label={label}
          onChange={e => onChange(fieldKey, e.target.value)}
          sx={{ borderRadius: '8px', fontSize: '0.875rem' }}
        >
          {options.map(opt => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Boolean — toggle switch
  if (isBoolean) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '4px' }}
      >
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[700] }}>{label}</Typography>
        <Switch
          checked={Boolean(value ?? schemaParam?.default)}
          onChange={e => onChange(fieldKey, e.target.checked)}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' },
          }}
        />
      </Box>
    );
  }

  // JSON / object fields
  const displayValue = isJson
    ? typeof value === 'object'
      ? JSON.stringify(value, null, 2)
      : value ?? '{}'
    : value ?? '';

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      InputLabelProps={{ shrink: true }}
      type={isNumber ? 'number' : 'text'}
      multiline={isLong || isJson}
      minRows={isJson ? 4 : isLong ? 2 : undefined}
      value={displayValue}
      placeholder={placeholder}
      onChange={e => {
        if (isNumber) {
          onChange(fieldKey, e.target.value === '' ? '' : Number(e.target.value));
        } else {
          onChange(fieldKey, e.target.value);
        }
      }}
      helperText={helperText || (isJson ? 'JSON object' : undefined)}
      sx={INPUT_SX}
      inputProps={
        fieldKey === 'threshold'
          ? { step: 0.01, min: 0, max: 1 }
          : isNumber
          ? { step: 1 }
          : undefined
      }
    />
  );
}

// ── Add / Edit modal ───────────────────────────────────────────────────────────

const EMPTY_FORM = { display_name: '', type: '', params: {} };

function ToolModal({ open, onClose, onSubmit, toolTypes, tools, editTool, saving, saveError }) {
  const isEdit = Boolean(editTool);
  const [form, setForm] = useState(EMPTY_FORM);
  const [displayNameError, setDisplayNameError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        name: editTool.name,
        display_name: editTool.display_name || '',
        type: editTool.type,
        params: { ...editTool.params },
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setDisplayNameError('');
  }, [open, isEdit, editTool]);

  // Singleton types already in use are hidden in Add mode; always keep current type in Edit mode
  const existingTypeSet = new Set(tools.map(t => t.type));
  const availableTypes = toolTypes.filter(
    tt => !tt.singleton || !existingTypeSet.has(tt.type) || (isEdit && tt.type === editTool?.type)
  );

  // tool-types API returns { type, params: [...], singleton, requires_params }
  const selectedTypeEntry = toolTypes.find(t => t.type === form.type);
  const schemaParams = selectedTypeEntry?.params || [];
  const paramKeys = schemaParams.map(p => p.field_name);

  const handleTypeChange = type => {
    const entry = toolTypes.find(t => t.type === type);
    const params = entry?.params || [];
    const defaultParams = params.reduce((acc, p) => {
      if (p.default !== undefined && p.default !== null) acc[p.field_name] = p.default;
      return acc;
    }, {});
    setForm(f => ({ ...f, type, params: defaultParams }));
  };

  const handleParamChange = (key, value) => {
    setForm(f => ({ ...f, params: { ...f.params, [key]: value } }));
  };

  const handleSubmit = () => {
    if (!form.display_name.trim()) {
      setDisplayNameError('Display name is required');
      return;
    }
    if (!form.type) {
      return;
    }

    // JSON fields must be sent as JSON strings — stringify objects, keep strings as-is
    const params = { ...form.params };
    JSON_FIELDS.forEach(k => {
      if (typeof params[k] === 'object' && params[k] !== null) {
        params[k] = JSON.stringify(params[k]);
      }
    });
    onSubmit({ name: form.name, display_name: form.display_name.trim(), type: form.type, params });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
          {isEdit ? 'Edit Tool' : 'Add Tool'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400] }}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {/* Type selector */}
        <FormControl fullWidth size="small" disabled={isEdit}>
          <InputLabel sx={{ fontSize: '0.875rem' }}>Type</InputLabel>
          <Select
            value={form.type}
            label="Type"
            onChange={e => handleTypeChange(e.target.value)}
            sx={{ borderRadius: '8px', fontSize: '0.875rem' }}
          >
            {availableTypes.map(t => (
              <MenuItem key={t.type} value={t.type} sx={{ fontSize: '0.875rem' }}>
                {typeConfig(t.type).label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Edit-only: show internal tool name as read-only */}
        {isEdit && (
          <TextField
            fullWidth
            size="small"
            label="Tool Name"
            value={form.name || ''}
            disabled
            helperText="Internal name — cannot be changed after creation"
            sx={INPUT_SX}
          />
        )}

        {/* Display Name */}
        <TextField
          fullWidth
          size="small"
          label="Display Name"
          placeholder="e.g. Order Lookup"
          value={form.display_name}
          onChange={e => {
            setForm(f => ({ ...f, display_name: e.target.value }));
            setDisplayNameError('');
          }}
          error={Boolean(displayNameError)}
          helperText={displayNameError || undefined}
          sx={INPUT_SX}
        />

        {/* Dynamic params */}
        {paramKeys.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[700] }}>
              Parameters
            </Typography>
            {paramKeys.map(key => (
              <ParamField
                key={key}
                fieldKey={key}
                schemaParam={schemaParams.find(p => p.field_name === key)}
                value={form.params[key]}
                onChange={handleParamChange}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      {saveError && (
        <Box
          sx={{
            mx: 3,
            mb: 1,
            px: 2,
            py: '10px',
            bgcolor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', color: '#dc2626' }}>{saveError}</Typography>
        </Box>
      )}

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, borderTop: `1px solid ${SLATE[100]}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderColor: SLATE[300],
            color: SLATE[700],
            borderRadius: '8px',
            '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={saving || !form.type || !form.display_name.trim()}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none',
            boxShadow: 'none',
            borderRadius: '8px',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Tool'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete confirmation ────────────────────────────────────────────────────────

function DeleteDialog({ open, toolName, onClose, onConfirm, deleting }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], pb: 1 }}>
        Delete Tool?
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
          <strong>{toolName}</strong> will be permanently removed from this agent.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, borderTop: `1px solid ${SLATE[100]}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderColor: SLATE[300],
            color: SLATE[700],
            borderRadius: '8px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          size="small"
          color="error"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none',
            boxShadow: 'none',
            borderRadius: '8px',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Table header ───────────────────────────────────────────────────────────────

const COL = '220px 1fr 100px 130px';

function ColHeader({ label, sx }) {
  return (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        color: SLATE[400],
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        ...sx,
      }}
    >
      {label}
    </Typography>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function Capabilities() {
  const { id: agentId, app_name: appName } = useParams();
  const tokens = useSelector(s => s?.tokens.value);
  const currentApp = useSelector(s => s?.apps?.selectedApp);
  const apps = useSelector(s => s?.apps?.list || []);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const app = currentApp || apps.find(a => a.url_code === appName);
  const appId = app?.id;

  // Keep a ref so fetchData always has the latest tokens without them being a dep
  const tokensRef = useRef(tokens);
  useEffect(() => { tokensRef.current = tokens; });

  const [loading, setLoading] = useState(true);
  const [toolTypes, setToolTypes] = useState([]);
  const [tools, setTools] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPageMeta({
      description: 'Manage tools that give your agent new abilities.',
      actions: (
        <Button
          variant="contained"
          startIcon={<AddOutlined sx={{ fontSize: '1rem' }} />}
          onClick={() => {
            setEditTool(null);
            setModalOpen(true);
          }}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Add Tool
        </Button>
      ),
    });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    const t = tokensRef.current;
    if (!appId || !agentId || !t) return;
    setLoading(true);
    try {
      const [typesResp, toolsResp] = await Promise.all([
        makeRequest(`/v1/apps/${appId}/agents/${agentId}/tool-types`, { method: 'GET', authTokens: t }),
        makeRequest(`/v1/apps/${appId}/agents/${agentId}/tools`,      { method: 'GET', authTokens: t }),
      ]);
      setToolTypes(typesResp?.tool_types || []);
      setTools(toolsResp?.tools || []);
    } catch {
      // toasted by useApiCall
    } finally {
      setLoading(false);
    }
  }, [appId, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async ({ name, display_name, type, params }) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editTool) {
        await makeRequest(`/v1/apps/${appId}/agents/${agentId}/tools/${editTool.name}`, {
          method: 'PUT',
          body: { display_name, params },
          authTokens: tokens,
        });
      } else {
        await makeRequest(`/v1/apps/${appId}/agents/${agentId}/tools`, {
          method: 'POST',
          body: { display_name, type, params },
          authTokens: tokens,
        });
      }
      setSaveError(null);
      setModalOpen(false);
      await fetchData();
    } catch (err) {
      setSaveError(
        err?.response?.error ||
          err?.error ||
          err?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await makeRequest(`/v1/apps/${appId}/agents/${agentId}/tools/${deleteTarget.name}`, {
        method: 'DELETE',
        authTokens: tokens,
      });
      setDeleteTarget(null);
      await fetchData();
    } catch {
      // toasted
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading tools..." />;

  return (
    <>
      <Box
        sx={{
          bgcolor: '#fff',
          border: `1px solid ${SLATE[200]}`,
          borderRadius: '16px',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: COL,
            alignItems: 'center',
            px: 3,
            py: '14px',
            borderBottom: `1px solid ${SLATE[200]}`,
            bgcolor: SLATE[50],
          }}
        >
          <ColHeader label="Tool Name" />
          <ColHeader label="Description" />
          <ColHeader label="Type" sx={{ textAlign: 'center' }} />
          <Box />
        </Box>

        {/* Empty state */}
        {tools.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ fontSize: '2rem', mb: 1 }}>🔧</Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], mb: '6px' }}>
              No tools yet
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: SLATE[500], mb: 3 }}>
              Add tools to give your agent new abilities.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => {
                setEditTool(null);
                setModalOpen(true);
              }}
              sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              Add Tool
            </Button>
          </Box>
        )}

        {/* Tool rows */}
        {tools.map((tool, idx) => {
          const cfg = typeConfig(tool.type);
          const description = tool.params?.tool_description || '—';
          const isLast = idx === tools.length - 1;

          return (
            <Box
              key={tool.name}
              sx={{
                display: 'grid',
                gridTemplateColumns: COL,
                alignItems: 'center',
                px: 3,
                py: '18px',
                borderBottom: isLast ? 'none' : `1px solid ${SLATE[100]}`,
                transition: 'background 0.12s',
                '&:hover': { bgcolor: SLATE[50] },
              }}
            >
              {/* Name + icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 2 }}>
                <cfg.Icon sx={{ fontSize: '1.1rem', color: cfg.color, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: SLATE[900],
                      wordBreak: 'break-word',
                    }}
                  >
                    {tool.display_name || tool.name}
                  </Typography>
                  {tool.display_name && (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: SLATE[400], wordBreak: 'break-all' }}
                    >
                      {tool.name}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: SLATE[500],
                  pr: 3,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {description}
              </Typography>

              {/* Type badge */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    px: '10px',
                    py: '3px',
                    borderRadius: '100px',
                    border: `1px solid ${cfg.color}30`,
                    bgcolor: cfg.bg,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: cfg.color,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cfg.label}
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditTool(tool);
                    setModalOpen(true);
                  }}
                  sx={{ color: SLATE[500], '&:hover': { color: INDIGO[600], bgcolor: INDIGO[50] } }}
                >
                  <EditOutlined sx={{ fontSize: '1rem' }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setDeleteTarget(tool)}
                  sx={{ color: SLATE[500], '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
                >
                  <DeleteOutlineOutlined sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Add / Edit modal */}
      <ToolModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSaveError(null);
        }}
        onSubmit={handleSave}
        toolTypes={toolTypes}
        tools={tools}
        editTool={editTool}
        saving={saving}
        saveError={saveError}
      />

      {/* Delete confirmation */}
      <DeleteDialog
        open={Boolean(deleteTarget)}
        toolName={deleteTarget?.display_name || deleteTarget?.name}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleting={deleting}
      />
    </>
  );
}
